import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, CreditCard, Pencil, Plus, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { AppIcons } from '../../components/AppIcon';
import { accountApps, appNames } from '../../utils/accounts';
import { Button } from '../../components/ui/Button';
import { Badge, Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { ResourceForm } from '../../forms/ResourceForm';
import { useLookups } from '../../hooks/useLookups';
import type { CreditUsage, ManagerAccount, PrepaidBalance } from '../../types/models';
import { formatMoney, formatPercent } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { managerAccountsConfig } from '../masters/configs';
import { CreditBar, PaymentDialog } from './components';

type PaymentTarget = { kind: 'mcc' | 'prepaid'; id: string; name: string } | null;

/** One MCC with its linked Google Ads accounts underneath (tree row). */
function MccTreeItem({
  usage,
  canWrite,
  canPay,
  onEdit,
  onPay,
}: {
  usage: CreditUsage;
  canWrite: boolean;
  canPay: boolean;
  onEdit: () => void;
  onPay: () => void;
}) {
  const [open, setOpen] = useState(true);
  const m = usage.managerAccount;
  const hasOpening = Number(usage.openingOutstanding) > 0;
  return (
    <li className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-start gap-4 p-4">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? 'Collapse' : 'Expand'}
          className="mt-0.5 rounded-md p-1 text-slate-500 hover:bg-slate-100"
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <div className="min-w-56 flex-1">
          <Link
            to={`/mcc-accounts/${m.id}`}
            className="font-semibold text-slate-900 hover:text-brand-700"
          >
            {m.name}
          </Link>
          <p className="text-xs text-slate-500">
            {[m.platform.name, m.accountIdentifier, m.loginEmail].filter(Boolean).join(' · ')}
          </p>
          <p className="text-xs text-slate-500">
            GST: {m.gstAccount.tradeName || m.gstAccount.legalName} ·{' '}
            <span className="font-mono">{m.gstAccount.gstin}</span>
          </p>
        </div>
        <div className="w-full sm:w-72">
          <CreditBar used={usage.used} limit={usage.creditLimit} />
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">Available</p>
          <p
            className={clsx(
              'num text-lg font-semibold',
              usage.available && Number(usage.available) < 0 ? 'text-rose-600' : 'text-emerald-700',
            )}
          >
            {formatMoney(usage.available)}
          </p>
        </div>
        <div className="flex gap-1">
          {canPay && (
            <Button
              size="sm"
              variant="secondary"
              icon={<CreditCard className="size-4" />}
              onClick={onPay}
            >
              Payment
            </Button>
          )}
          {canWrite && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Pencil className="size-4" />}
              onClick={onEdit}
              aria-label="Edit MCC"
            />
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100">
          {usage.accounts.length === 0 && !hasOpening ? (
            <p className="px-12 py-4 text-sm text-slate-500">No advertising accounts linked yet.</p>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="min-w-[15rem] py-2 pr-4 pl-12 text-left font-semibold">
                      Linked account
                    </th>
                    <th className="px-4 py-2 text-left font-semibold">Login email</th>
                    <th className="px-4 py-2 text-left font-semibold">Billing</th>
                    <th className="px-4 py-2 text-right font-semibold">Spend this month</th>
                    <th className="px-4 py-2 text-right font-semibold">Credit used</th>
                    <th className="px-4 py-2 text-right font-semibold">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {usage.accounts.map((a) => (
                    <tr key={a.account.id} className={clsx(!a.account.linked && 'text-slate-400')}>
                      <td className="py-2 pr-4 pl-12">
                        <span className="flex items-center gap-2">
                          <span aria-hidden className="text-slate-300">
                            └
                          </span>
                          <AppIcons apps={accountApps(a.account)} size={22} />
                          <span>
                            <Link
                              to={`/ads-accounts/${a.account.id}`}
                              className="text-slate-900 hover:text-brand-700"
                            >
                              {a.account.accountName}
                            </Link>
                            <span className="block text-xs text-slate-400">
                              {[appNames(a.account), a.account.accountIdentifier]
                                .filter(Boolean)
                                .join(' · ')}
                              {!a.account.linked && ' · no longer linked'}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">{a.account.loginEmail ?? '—'}</td>
                      <td className="px-4 py-2">
                        <Badge tone={a.account.billingType === 'POSTPAID' ? 'violet' : 'blue'}>
                          {a.account.billingType === 'POSTPAID' ? 'Credit line' : 'Prepaid'}
                        </Badge>
                      </td>
                      <td className="num px-4 py-2 text-right">{formatMoney(a.spendThisMonth)}</td>
                      <td className="num px-4 py-2 text-right font-medium text-slate-900">
                        {formatMoney(a.outstanding)}
                      </td>
                      <td className="num px-4 py-2 text-right">
                        {formatPercent(a.shareOfUsedPercent)}
                      </td>
                    </tr>
                  ))}
                  {hasOpening && (
                    <tr className="text-slate-500">
                      <td className="py-2 pr-4 pl-12 italic" colSpan={4}>
                        <span aria-hidden className="mr-2 text-slate-300">
                          └
                        </span>
                        Opening balance (used before tracking started, not split by account)
                      </td>
                      <td className="num px-4 py-2 text-right">
                        {formatMoney(usage.openingOutstanding)}
                      </td>
                      <td className="num px-4 py-2 text-right">
                        {formatPercent(usage.openingOutstandingSharePercent)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function ManagerAccountsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: lookups } = useLookups();
  const [editing, setEditing] = useState<ManagerAccount | 'new' | null>(null);
  const [payment, setPayment] = useState<PaymentTarget>(null);

  const overview = useQuery({
    queryKey: ['billing', 'credit-overview'],
    queryFn: () => api.get<CreditUsage[]>('/manager-accounts/credit-overview'),
  });
  const prepaid = useQuery({
    queryKey: ['billing', 'prepaid-overview'],
    queryFn: () => api.get<PrepaidBalance[]>('/advertising-accounts/prepaid-overview'),
  });

  const canWrite = can('masters.write');
  const canPay = can('transactions.write');

  const openEdit = async (id: string) =>
    setEditing(await api.get<ManagerAccount>(`/mcc-accounts/${id}`));

  const totalLimit = (overview.data ?? []).reduce((s, u) => s + Number(u.creditLimit ?? 0), 0);
  const totalUsed = (overview.data ?? []).reduce((s, u) => s + Number(u.used), 0);

  return (
    <>
      <PageHeader
        title="MCC & credit lines"
        description="Each manager account (MCC) and the Google Ads accounts linked under it, with credit used, available and each account's share."
        actions={
          canWrite && (
            <Button icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
              Add MCC
            </Button>
          )
        }
      />

      {overview.isLoading ? (
        <Spinner />
      ) : overview.data?.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CreditCard className="size-6" />}
            title="No manager accounts yet"
            description="Add your Google Ads MCC with its credit limit, then link postpaid Google Ads accounts to it."
          />
        </Card>
      ) : (
        <>
          {overview.data && overview.data.length > 1 && (
            <p className="num mb-3 text-sm text-slate-600">
              All credit lines: {formatMoney(String(totalUsed))} used of{' '}
              {formatMoney(String(totalLimit))}
            </p>
          )}
          <ul className="space-y-4">
            {overview.data?.map((u) => (
              <MccTreeItem
                key={u.managerAccount.id}
                usage={u}
                canWrite={canWrite}
                canPay={canPay}
                onEdit={() => void openEdit(u.managerAccount.id)}
                onPay={() =>
                  setPayment({ kind: 'mcc', id: u.managerAccount.id, name: u.managerAccount.name })
                }
              />
            ))}
          </ul>
        </>
      )}

      <Card
        className="mt-8"
        title={
          <span className="flex items-center gap-2">
            <Wallet className="size-4 text-slate-400" /> Prepaid accounts
          </span>
        }
        description="Balance = opening balance + top-ups − spend. Days left uses last week's average daily spend."
        bodyClassName="p-0"
      >
        {prepaid.isLoading ? (
          <Spinner />
        ) : prepaid.data?.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No active prepaid accounts.</p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Account</th>
                  <th className="px-4 py-3 text-left font-semibold">Login email</th>
                  <th className="px-4 py-3 text-right font-semibold">Top-ups</th>
                  <th className="px-4 py-3 text-right font-semibold">Spend</th>
                  <th className="px-4 py-3 text-right font-semibold">Balance</th>
                  <th className="px-4 py-3 text-right font-semibold">Days left</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {prepaid.data?.map((p) => {
                  const low =
                    Number(p.balance) <= 0 ||
                    (p.daysRemaining != null && Number(p.daysRemaining) < 3);
                  return (
                    <tr key={p.account.id}>
                      <td className="px-4 py-2">
                        <span className="flex items-center gap-2">
                          <AppIcons apps={accountApps(p.account)} size={22} />
                          <span>
                            <Link
                              to={`/ads-accounts/${p.account.id}`}
                              className="text-slate-900 hover:text-brand-700"
                            >
                              {p.account.accountName}
                            </Link>
                            <span className="block text-xs text-slate-400">
                              {appNames(p.account)}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">{p.account.loginEmail ?? '—'}</td>
                      <td className="num px-4 py-2 text-right">{formatMoney(p.totalTopups)}</td>
                      <td className="num px-4 py-2 text-right">{formatMoney(p.totalSpend)}</td>
                      <td
                        className={clsx(
                          'num px-4 py-2 text-right font-semibold',
                          low ? 'text-rose-600' : 'text-slate-900',
                        )}
                      >
                        {formatMoney(p.balance)}
                      </td>
                      <td className="num px-4 py-2 text-right">{p.daysRemaining ?? '—'}</td>
                      <td className="px-4 py-2 text-right">
                        {canPay && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() =>
                              setPayment({
                                kind: 'prepaid',
                                id: p.account.id,
                                name: p.account.accountName,
                              })
                            }
                          >
                            Top up
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PaymentDialog target={payment} onClose={() => setPayment(null)} />

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add manager account (MCC)' : 'Edit manager account'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" form="mcc-form">
              {editing === 'new' ? 'Create' : 'Save changes'}
            </Button>
          </>
        }
      >
        {editing && (
          <ResourceForm
            key={editing === 'new' ? 'new' : editing.id}
            formId="mcc-form"
            fields={managerAccountsConfig.fields}
            schema={managerAccountsConfig.schema}
            isEdit={editing !== 'new'}
            lookups={lookups}
            defaultValues={
              editing === 'new'
                ? managerAccountsConfig.defaults(lookups)
                : managerAccountsConfig.toForm(editing)
            }
            onSubmit={async (values) => {
              if (editing === 'new') await api.post('/manager-accounts', values);
              else
                await api.put(`/mcc-accounts/${editing.id}`, {
                  ...values,
                  updatedAt: editing.updatedAt,
                });
              toast.success(editing === 'new' ? 'MCC created' : 'MCC updated');
              setEditing(null);
              void queryClient.invalidateQueries({ queryKey: ['billing'] });
              void queryClient.invalidateQueries({ queryKey: ['lookups'] });
            }}
          />
        )}
      </Modal>
    </>
  );
}
